/**
 * Turning a finished analysis into the one thing the desk receives.
 *
 * By the time this runs the frames, the landmarks, the outline and the eight ratios have all gone out
 * of scope. What leaves is a shape, two scores, reason codes and three version strings — and there is
 * no field here for anything else, which is the point.
 *
 * The submission identity is the attempt's own id rather than a fresh one per click, so one capture
 * has one identity for its whole life: re-sending it is idempotent, and two attempts can never share
 * an identity to reuse across sessions.
 */
import { FaceShape } from '@optiflow/face-vision';
import type { FaceCandidateInput, NamedFaceShape } from '@optiflow/types';
import { coarseDeviceClass } from '@/modules/capture/captureClient';
import { FACE_VERSIONS, type FaceAttempt } from './vision/useFaceCapture';

/**
 * Null when there is nothing to offer, for either of two reasons.
 *
 * The capture settled on nothing — a suggestion that says nothing is not worth a person's time — or
 * the result belongs to a different session from the one asking. The second is the harder one: a
 * result captured for session A must never be offered for session B, however alike the two look.
 */
export function candidateFrom(
  attempt: FaceAttempt,
  sessionKey: string,
  submissionId: string = attempt.attemptId,
): FaceCandidateInput | null {
  if (attempt.sessionKey !== sessionKey) return null;
  if (!attempt.usable) return null;

  const { analysis } = attempt;
  const isMixed = analysis.suggested === FaceShape.MIXED;

  return {
    submissionId,
    suggestedShape: analysis.suggested,
    ...(isMixed
      ? {
          suggestedComponentA: analysis.components[0] as NamedFaceShape,
          suggestedComponentB: analysis.components[1] as NamedFaceShape,
        }
      : {}),
    ...(analysis.runnerUp ? { runnerUpShape: analysis.runnerUp as NamedFaceShape } : {}),
    topRuleMatchScoreBp: analysis.topScoreBp,
    runnerUpRuleMatchScoreBp: analysis.runnerUpScoreBp,
    reasonCodes: analysis.reasons,
    ...FACE_VERSIONS,
    acceptedFrameCount: attempt.acceptedFrameCount,
    captureDurationMs: attempt.captureDurationMs,
    deviceClass: coarseDeviceClass(),
  };
}
